import type { Writable } from "svelte/store";
import type { DrimeConfig } from "./fs";
import { FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// Drime does not answer other origins. Calls go through /api/drime, which
// talks to https://app.drime.cloud. The access token is a request header.
// What is deleted goes to Drime's trash.

export const prop_drime_mime = "drime_mime";
export const prop_drime_id = "drime_id";

class FileSystemDrime extends FS {
  supportsDirRename = true;

  constructor(config: DrimeConfig) {
    super(config);
  }

  get accessToken(): string {
    return (this.config as DrimeConfig).accessToken;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let js = await drimeCall(this.accessToken, mkURL("/api/drime/list", "dir", dirPath));
      if (!Array.isArray(js)) {
        throw new Error("drime list failed");
      }
      return FsDir.fromWire(dirPath, js);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    // Preview and the download link cannot send the token. Fetch the file
    // and hand the bytes back as a blob URL.
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let url = mkURL("/api/drime/download", "path", path);
    let xhr = await drimeSend("GET", url, this.accessToken, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let url = mkURL("/api/drime/upload", "path", path);
    await drimeSend("PUT", url, this.accessToken, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = mkURL("/api/drime/rename", "path", path, "newPath", newPath);
    await drimeCall(this.accessToken, url, "POST");
  }

  async createDirectory(dirPath: string): Promise<void> {
    await drimeCall(this.accessToken, mkURL("/api/drime/mkdir", "path", dirPath), "POST");
  }

  // To the trash. A folder goes with everything in it.
  async deleteFile(path: string): Promise<void> {
    await drimeCall(this.accessToken, mkURL("/api/drime/delete", "path", path), "POST");
  }
}

export async function testDrimeConnection(accessToken: string): Promise<void> {
  await drimeCall(accessToken, "/api/drime/test");
}

async function drimeCall(accessToken: string, url: string, method = "GET"): Promise<any> {
  let rsp = await fetch(url, {
    method,
    headers: { "X-Drime-Token": accessToken },
  });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `drime ${rsp.status}`);
  }
  return js;
}

function drimeSend(
  method: string,
  url: string,
  accessToken: string,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    xhr.setRequestHeader("X-Drime-Token", accessToken);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      drimeErrText(xhr).then(
        (msg) => reject(new Error(msg)),
        () => reject(new Error(`drime ${xhr.status}`)),
      );
    };
    xhr.onerror = () => {
      reject(new Error("drime request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function drimeErrText(xhr: XMLHttpRequest): Promise<string> {
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `drime ${xhr.status}`;
}

export function makeFileSystemDrime(config: DrimeConfig): FileSystemDrime {
  return new FileSystemDrime(config);
}
